import { useCallback, useEffect, useRef, useState } from 'react';
import { useResendVerificationEmail } from '../mutations/useResendVerificationEmail';
import { useTurnstile } from './useTurnstile';

export const useEmailVerificationResend = (email?: string, sendOnOpen = false) => {
  const turnstile = useTurnstile('resend_email');
  const { run } = turnstile;
  const { mutateAsync, isPending } = useResendVerificationEmail();
  const initialSendStarted = useRef(false);
  const [countdown, setCountdown] = useState(0);
  const [requestError, setRequestError] = useState<'failed' | 'unavailable' | 'unexpectedError' | null>(null);

  useEffect(() => {
    if (countdown <= 0) {
      return;
    }

    const timer = setTimeout(() => setCountdown((value) => value - 1), 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  const resend = useCallback(async () => {
    if (!email || countdown > 0 || isPending) {
      return false;
    }

    return run((turnstileToken) => {
      setRequestError(null);

      return mutateAsync(
        { email, turnstileToken },
        {
          onSuccess: () => setCountdown(30),
          onError: (error) => {
            const code = error.response?.data?.errors?.[0]?.code;

            if (code === 'CAPTCHA_UNAVAILABLE') {
              setRequestError('unavailable');
            } else if (code === 'CAPTCHA_FAILED') {
              setRequestError('failed');
            } else {
              setRequestError('unexpectedError');
            }
          },
        }
      );
    });
  }, [countdown, email, isPending, mutateAsync, run]);

  useEffect(() => {
    if (!sendOnOpen || !email || !turnstile.isReady || initialSendStarted.current) {
      return;
    }

    initialSendStarted.current = true;
    void resend();
  }, [email, resend, sendOnOpen, turnstile.isReady]);

  return {
    turnstile,
    countdown,
    requestError,
    resend,
    isDisabled: !email || isPending || countdown > 0 || !turnstile.isReady,
  };
};
